import userProfileService from '@/services/userProfileService';

const state = {
  profile: null,
  loading: false,
  error: null,
  lastFetched: null
};

const mutations = {
  SET_PROFILE(state, profile) {
    state.profile = profile;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_LAST_FETCHED(state, timestamp) {
    state.lastFetched = timestamp;
  }
};

const actions = {
  async fetchUserProfile({ commit, state }) {
    // Check if we already have the data and it's recent (less than 5 minutes old)
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (state.profile && state.lastFetched && (now - state.lastFetched < fiveMinutes)) {
      // We have recent data, no need to fetch again
      return state.profile;
    }
    
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const profile = await userProfileService.getUserProfile();
      commit('SET_PROFILE', profile);
      commit('SET_LAST_FETCHED', now);
      return profile;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async updateUserProfile({ commit }, profileData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const updatedProfile = await userProfileService.updateUserProfile(profileData);
      
      // Update the profile data in the store directly to reflect new changes immediately
      commit('SET_PROFILE', updatedProfile);
      commit('SET_LAST_FETCHED', Date.now());
      
      return updatedProfile;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

const getters = {
  userProfile: state => state.profile,
  isProfileLoading: state => state.loading,
  profileError: state => state.error,
  
  // Specific profile fields for convenience
  displayUserName: state => state.profile?.displayUserName || '',
  email: state => state.profile?.email || '',
  phoneNumber: state => {
    const rawNumber = state.profile?.phoneNumber || '';
    
    // Clean the phone number by removing any non-digit characters
    let cleanNumber = rawNumber.replace(/\D/g, '');
    
    // If it starts with country code (90), remove it - we'll add it back in the UI
    if (cleanNumber.startsWith('90')) {
      cleanNumber = cleanNumber.substring(2);
    }
    
    // Log the clean number for debugging
    return cleanNumber;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}; 